/**
 * useMultiCropTaskData Hook
 *
 * Fetches tasks for multiple crops using single aggregate query.
 * Pattern: Mirrors useMultiCropScheduleData structure.
 */

import { useState, useEffect, useMemo } from 'react'
import { supabaseGetter } from '~/api/supabase-getter'
import { useCropsContext } from '~/common/context/crops/crops-context'

/**
 * Fetches tasks for multiple crops
 *
 * @param {Object} params
 * @param {Array} params.crops - Array of crop objects with { id, ... }
 * @returns {Object} { taskData, loading, error, invalidate }
 * taskData format: [{ crop, tasks: [...] }]
 */
export const useMultiCropTaskData = ({ crops }) => {
  const [taskData, setTaskData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const cropsContext = useCropsContext()
  const { refreshKey, lastTaskUpdate } = cropsContext

  // Extract crop IDs from crops array
  const cropIds = useMemo(
    () => (crops || []).map(c => c.id),
    [crops]
  )

  // Update a specific task in the local state (optimistic update)
  const updateTaskInData = useMemo(() => (taskId, updates) => {
    setTaskData(prevData =>
      prevData.map(cropData => ({
        ...cropData,
        tasks: cropData.tasks.map(task =>
          task.id === taskId
            ? { ...task, ...updates }
            : task
        )
      }))
    )
  }, [])

  // Apply optimistic task updates broadcast from other components
  useEffect(() => {
    if (!lastTaskUpdate) return
    updateTaskInData(lastTaskUpdate.taskId, lastTaskUpdate.updates)
  }, [lastTaskUpdate])

  useEffect(() => {
    if (!crops || crops.length === 0) {
      setTaskData([])
      setLoading(false)
      setError(null)
      return
    }

    async function fetchTasks() {
      setLoading(true)
      setError(null)

      try {
        // Fetch tasks by crop IDs
        const { data: tasksData, error: fetchError } =
          await supabaseGetter.tasks.getByCrops(cropIds)

        if (fetchError) throw fetchError

        // Client-side join: Group tasks by crop
        const cropsMap = new Map(crops.map(crop => [crop.id, crop]))
        const tasksByCrop = new Map()

        // Initialize empty arrays for all crops
        crops.forEach(crop => {
          tasksByCrop.set(crop.id, [])
        })

        // Group tasks by crop_id
        tasksData?.forEach(task => {
          if (task.crop_id && tasksByCrop.has(task.crop_id)) {
            tasksByCrop.get(task.crop_id).push(task)
          }
        })

        // Combine crops with their tasks
        const combined = crops.map(crop => ({
          crop,
          tasks: tasksByCrop.get(crop.id) || []
        }))

        setTaskData(combined)
      } catch (err) {
        console.error('Error fetching crop tasks:', err)
        setError(err.message)
        setTaskData([])
      } finally {
        setLoading(false)
      }
    }

    fetchTasks()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropIds.join(','), refreshKey])  // Use stabilized dependency + refreshKey for crop edits

  return {
    taskData,  // Array of { crop, tasks: [...] }
    loading,
    error,
    updateTaskInData,  // For manual updates (also registered as callback)
    invalidate: supabaseGetter.tasks.invalidate
  }
}
